import React from "react";
import { Link } from "react-router-dom";
import {
  Package,
  Tv,
  Truck,
  Trash2,
  PaintRoller,
  Wrench,
  CheckCircle2,
  ArrowRight,
  Phone,
  Star,
  Home as HomeIcon,
  Building2,
  KeyRound,
  Briefcase,
  ClipboardList,
} from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import Layout from "@/components/Layout";
import HeroPortfolioCarousel from "@/components/HeroPortfolioCarousel";
import { BUSINESS } from "@/lib/api";

const services = [
  {
    slug: "furniture-assembly",
    icon: Package,
    name: "Furniture Assembly",
    body: "From beds and desks to cabinets and shelving, we assemble your furniture correctly and save you hours of frustration.",
    detail:
      "New furniture shouldn't take all day to put together. We assemble beds, desks, tables, cabinets, shelving, and more with care and precision.",
    perfectFor: ["New home setups", "Apartment moves", "Office furniture", "Rental properties"],
  },
  {
    slug: "tv-mounting",
    icon: Tv,
    name: "TV Mounting & Wall Installation",
    body: "Professional TV mounting, shelves, mirrors, artwork, curtain rods, and more.",
    detail:
      "Create a cleaner, more functional space with secure TV mounting and wall installations.",
    perfectFor: ["TV mounting", "Shelves", "Mirrors", "Artwork", "Curtain rods", "Wall accessories"],
  },
  {
    slug: "moving-help",
    icon: Truck,
    name: "Moving Assistance",
    body: "Need help lifting, loading, unloading, or rearranging? We provide an extra set of hands when you need it.",
    detail:
      "Not every move requires a full moving company. We provide help with the heavy lifting and setup.",
    perfectFor: ["Loading & unloading", "Furniture placement", "Rearranging rooms", "Move-in setup"],
  },
  {
    slug: "junk-removal",
    icon: Trash2,
    name: "Junk Removal",
    body: "Remove unwanted furniture, boxes, clutter, and leftover items before or after your move.",
    detail:
      "Moving is the perfect time to remove clutter. We help you clear unwanted items and leave your space clean and ready.",
    perfectFor: ["Furniture", "Boxes", "Household items", "General clutter"],
  },
  {
    slug: "painting",
    icon: PaintRoller,
    name: "Painting Services",
    body: "Freshen up your home with interior painting, touch-ups, and move-out repairs.",
    detail:
      "Whether you're moving in, moving out, or updating your home, we provide clean and reliable painting services.",
    perfectFor: ["Interior painting", "Touch-ups", "Move-out repairs", "Accent walls"],
  },
  {
    slug: "general-handyman",
    icon: Wrench,
    name: "General Handyman Work",
    body: "Small repairs, installations, and home improvement tasks handled professionally.",
    detail:
      "Drywall patches, fixture swaps, hardware installs, the whole honey-do list — we handle it.",
    perfectFor: ["Drywall patching", "Light fixtures", "Door hardware", "Small repairs"],
  },
];

const whyUs = [
  "Multiple services from one trusted team",
  "No need to hire several contractors",
  "Respectful of your home and belongings",
  "Quality work with attention to detail",
  "Convenient scheduling",
];

const personas = [
  { slug: "homeowners", icon: HomeIcon, label: "Homeowners", body: "Complete your project list without the hassle." },
  { slug: "renters", icon: KeyRound, label: "Renters", body: "Get your new place set up or leave your old place move-out ready." },
  { slug: "landlords", icon: Building2, label: "Landlords", body: "Prepare rental properties quickly between tenants." },
  { slug: "realtors", icon: Briefcase, label: "Realtors", body: "Help clients get homes ready for showings and closing." },
  { slug: "property-managers", icon: ClipboardList, label: "Property Managers", body: "Reliable help for ongoing maintenance needs." },
];

const reviews = [
  {
    service: "Furniture Assembly",
    quote:
      "I recently had the pleasure of having Willie complete some work in my home, and I can’t speak highly enough of him! He was superb in every aspect. Willie worked quickly and efficiently, demonstrating a strong work ethic and attention to detail. Not only was he polite and respectful throughout the job, but he also made sure to clean up thoroughly once he was finished. I would wholeheartedly recommend Willie to anyone looking for quality work — truly a good worker and a delightful gentleman!",
  },
  {
    service: "Furniture Assembly",
    quote:
      "I’ve never had anyone brighten up not only my room but my day, while continuously working super hard and focused, like Alejandro did. So much care and attention to detail. Such a skilled and genuine person — I’ll never hire anyone else after what he did for me today!",
  },
  {
    service: "Furniture Assembly",
    quote:
      "Willie was fantastic! I had a large kids bunk bed with a slide, drawers, and stairs. He did awesome work! He got to my house earlier than expected and didn’t waste a single second of the time he was here. I highly recommend him — 10 out of 10! He was pleasant, professional, and wonderful to work with!",
  },
  {
    service: "TV Mounting",
    quote:
      "Willie was very professional and detailed. He suggested I hang my TV on a different wall from the one I wanted it on, and I couldn’t be happier with his suggestion. I will most definitely book Willie again.",
  },
  {
    service: "Furniture Assembly",
    quote:
      "Please use this tasker! He is extremely energetic and knowledgeable about putting furniture together. I’m pretty handy myself but I looked at the install instructions and hired Alejandro — so glad I did. I really believe it would have taken me a day and a half to do what he did in 4 or 5 hours.",
  },
  {
    service: "General Mounting",
    quote:
      "I’m happy I hired Willie to put up my gazebo. He is very thorough and did a very good job! I will hire him again.",
  },
  {
    service: "Furniture Assembly",
    quote:
      "Such a great and hardworking individual. I would trust him with anything needed to be done. He goes above and beyond to do everything right. He’s also very nice to talk with and will work with other workers too to get the job done!",
  },
  {
    service: "Heavy Lifting & Loading",
    quote:
      "Willie showed up on time and was ready to work. He hit the ground running, was full of energy, and performed the task of unloading a 26-foot truck in a professional and efficient manner. Great attitude throughout the entire process — we would definitely use him again.",
  },
  {
    service: "Furniture Assembly",
    quote:
      "Alejandro was great for a ginormous job of assembling an 18-foot Jumpking trampoline with basketball hoops. He was great at directing and teaching me. We got it done in 4.5 hours instead of fumbling through it over 2 days. Well worth the money — would definitely hire again!",
  },
  {
    service: "Heavy Lifting & Loading",
    quote:
      "Willie was amazing — very punctual, and came ready to work!!!",
  },
  {
    service: "Furniture Assembly",
    quote:
      "Excellent tasker. Takes pride in his work and pays attention to detail. Outgoing personality. Highly recommend!",
  },
  {
    service: "Help Moving",
    quote:
      "Willie was amazing to work with! He was timely, efficient, and had a smile on his face the whole time. He helped me move and set up things in my new home. I highly recommend him!",
  },
  {
    service: "Furniture Assembly",
    quote:
      "Alejandro arrived on time. He was courteous and friendly to the entire staff. He quickly built our couch in less time than we expected. We will use his services again in the future.",
  },
  {
    service: "Furniture Assembly",
    quote:
      "Great assembler. Fast and items were very well made.",
  },
  {
    service: "Furniture Assembly",
    quote:
      "Alejandro was fantastic! He had a wealth of knowledge about his work and paid great attention to detail.",
  },
  {
    service: "Furniture Assembly",
    quote:
      "Willie did an excellent job assembling an indoor sauna for us. He showed up on time and was very professional. The sauna was shipped in a 1,000 pound crate with approximately 200 different pieces, many of which looked similar — so the assembly was large and confusing. He was able to unload and organize all the parts and then assemble it very efficiently. Final assembled sauna looks great. Would highly recommend and definitely use again.",
  },
  {
    service: "Heavy Lifting & Loading",
    quote:
      "Service was amazing! He arrived with short notice, being that we needed help the next morning. He arrived on time and ready to dive in. He has a take-charge attitude and was not idle at any point, and made the best use of our time. 10/10 recommend and would use him again if I ever come back to Atlanta!",
  },
  {
    service: "Furniture Assembly",
    quote:
      "Fast and hard worker — pays attention to detail and very knowledgeable! Highly recommend.",
  },
  {
    service: "General Mounting",
    quote:
      "Willie came 100% on time and his work is impeccable. Great personality, very professional. He hung my mirrors, adjusted my painting and advised me on the best screws that should be used so my mirrors and painting will not fall off the wall and be ruined. Thank you Willie — keep continuing your awesome services!",
  },
  {
    service: "Furniture Assembly",
    quote:
      "Alejandro was top notch from beginning to end, getting straight to work and wasting no time! Highly recommend!",
  },
  {
    service: "Furniture Assembly",
    quote:
      "Amazing neat job! Would definitely utilize Willie’s services again. He did multiple tasks/mounting & assembly for my home. Thank you Willie.",
  },
  {
    service: "Furniture Assembly",
    quote:
      "Alejandro did a great job putting together two pieces of furniture for me. He was communicative ahead of time and got the tasks done quickly.",
  },
  {
    service: "General Mounting",
    quote:
      "Willie was excellent. He was efficient, thorough, and easy to work with. I will be using Willie every time I’m in ATL! Thank you Willie!",
  },
  {
    service: "Furniture Assembly",
    quote:
      "Alejandro was great! Very knowledgeable, professional, and kind. Would definitely recommend!",
  },
  {
    service: "Furniture Assembly",
    quote:
      "Asked if he could arrive an hour earlier than our scheduled appointment — got here and had the tasks knocked out expeditiously. Would definitely use again.",
  },
  {
    service: "Furniture Assembly",
    quote:
      "Alejandro did a great job. He was super friendly and helpful!",
  },
  {
    service: "Heavy Lifting & Loading",
    quote:
      "Willie worked really fast and was careful with moving all my items. He was very kind and got started right away. Very reliable working within our timeframe. Definitely would work with him again.",
  },
];

const REVIEW_STATS = {
  total: 308,
  average: 4.9,
};

function Stars() {
  return (
    <div className="flex items-center gap-0.5 text-amber-500">
      {[0, 1, 2, 3, 4].map((i) => (
        <Star key={i} className="h-4 w-4 fill-amber-500" strokeWidth={0} />
      ))}
    </div>
  );
}

function ReviewsCarousel() {
  const [api, setApi] = React.useState(null);
  const [paused, setPaused] = React.useState(false);

  React.useEffect(() => {
    if (!api || paused) return undefined;
    const id = setInterval(() => api.scrollNext(), 5000);
    return () => clearInterval(id);
  }, [api, paused]);

  return (
    <div
      className="mt-12 max-w-6xl mx-auto"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <Carousel
        opts={{ align: "start", loop: true }}
        setApi={setApi}
        data-testid="reviews-carousel"
      >
        <CarouselContent className="-ml-4">
          {reviews.map((r, i) => (
            <CarouselItem
              key={i}
              className="pl-4 md:basis-1/2 lg:basis-1/3"
              data-testid={`review-card-${i}`}
            >
              <figure className="h-full rounded-2xl border border-slate-100 bg-slate-50/50 p-7 flex flex-col">
                <Stars />
                <p className="mt-4 text-xs tracking-[0.18em] uppercase font-bold text-blue-700">
                  {r.service}
                </p>
                <blockquote className="mt-3 text-base text-slate-800 leading-relaxed flex-1">
                  “{r.quote}”
                </blockquote>
              </figure>
            </CarouselItem>
          ))}
        </CarouselContent>
        <div className="mt-8 flex items-center justify-center gap-3">
          <CarouselPrevious
            data-testid="reviews-prev"
            className="static translate-y-0 h-10 w-10"
          />
          <CarouselNext
            data-testid="reviews-next"
            className="static translate-y-0 h-10 w-10"
          />
        </div>
      </Carousel>
    </div>
  );
}

export default function HomePage() {
  return (
    <Layout>
      {/* SECTION 1 — HERO */}
      <section className="relative overflow-hidden pf-grid-bg">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 pf-fade-in">
            <span
              data-testid="hero-eyebrow"
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs uppercase tracking-[0.2em] font-bold text-slate-500"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Now booking in {BUSINESS.area}
            </span>
            <h1
              data-testid="hero-title"
              className="pf-heading mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]"
            >
              Move-In &amp; Move-Out <br className="hidden sm:block" />
              <span className="text-blue-700">Handyman Services</span> Made Simple.
            </h1>
            <p
              data-testid="hero-services-list"
              className="mt-6 text-sm sm:text-base text-slate-500 font-medium tracking-wide"
            >
              Furniture Assembly • TV Mounting • Moving Help • Junk Removal • Painting &amp; More
            </p>
            <p
              data-testid="hero-subtitle"
              className="mt-5 max-w-xl text-base sm:text-lg leading-relaxed text-slate-600"
            >
              Whether you’re moving into a new home, preparing a property for
              the next tenant, or tackling a long list of projects — we handle
              the work so you don’t have to.
            </p>

            <ul className="mt-8 space-y-3 max-w-md">
              {[
                "Fast & Reliable Service",
                "One Call Handles Multiple Jobs",
                "Residential & Property Management Services",
              ].map((item, i) => (
                <li
                  key={item}
                  data-testid={`hero-check-${i}`}
                  className="flex items-center gap-3 text-sm sm:text-base text-slate-700"
                >
                  <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/booking"
                data-testid="hero-cta-book"
                className="group inline-flex items-center gap-2 px-7 py-4 rounded-xl bg-slate-900 text-white font-semibold hover:bg-blue-700 transition-colors"
              >
                Book a Service
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/specials"
                data-testid="hero-cta-specials"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-xl bg-white text-slate-900 border border-slate-200 font-semibold hover:bg-slate-50 transition-colors"
              >
                View Current Specials
              </Link>
            </div>
            <p className="mt-4 text-xs text-slate-500">
              Call or text {BUSINESS.phone} — open {BUSINESS.hours.toLowerCase()}.
            </p>
          </div>

          <div className="lg:col-span-5">
            <HeroPortfolioCarousel />
          </div>
        </div>
      </section>

      {/* SECTION 2 — THE PROBLEM YOU SOLVE */}
      <section className="bg-white border-y border-slate-100">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-20 md:py-24 text-center">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">
            The Problem We Solve
          </p>
          <h2
            data-testid="problem-title"
            className="pf-heading mt-4 text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900"
          >
            Moving is already stressful. <br className="hidden sm:block" />
            Your to-do list doesn’t have to be.
          </h2>
          <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-3xl mx-auto">
            A move comes with hundreds of small tasks — assembling furniture,
            hanging TVs, fixing walls, removing unwanted items, and getting
            everything ready. We make the process easier by providing
            dependable handyman services all in one place.
          </p>
        </div>
      </section>

      {/* SECTION 3 — SERVICES */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">
              Our Services
            </p>
            <h2
              data-testid="services-title"
              className="pf-heading mt-3 text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900"
            >
              Everything you need, handled in one visit.
            </h2>
          </div>
          <Link
            to="/booking"
            data-testid="services-cta"
            className="inline-flex items-center gap-2 text-blue-700 font-semibold hover:text-blue-900"
          >
            Get a free quote
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <Link
                to={`/quote/${s.slug}`}
                key={s.name}
                data-testid={`service-card-${i}`}
                className="group bg-white border border-slate-100 rounded-2xl p-7 hover:border-blue-300 hover:shadow-[0_14px_40px_rgb(0,0,0,0.08)] hover:-translate-y-1 active:translate-y-0 transition-all duration-200 flex flex-col cursor-pointer"
              >
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-700 group-hover:bg-blue-700 group-hover:text-white transition-colors">
                  <Icon className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <h3 className="pf-heading mt-5 text-xl font-semibold text-slate-900">
                  {s.name}
                </h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">
                  {s.body}
                </p>
              </Link>
            );
          })}
        </div>

        {/* Expandable per-service details */}
        <div className="mt-16">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-3">
            Service Details
          </p>
          <h3 className="pf-heading text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">
            Click any service to see what&rsquo;s included.
          </h3>
          <Accordion
            type="single"
            collapsible
            className="mt-8 divide-y divide-slate-100 border border-slate-100 rounded-2xl bg-white"
            data-testid="service-accordion"
          >
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <AccordionItem
                  value={s.slug}
                  key={s.slug}
                  data-testid={`service-accordion-item-${i}`}
                  className="px-6 border-b-0 last:border-b-0"
                >
                  <AccordionTrigger className="py-5 hover:no-underline">
                    <div className="flex items-center gap-4 text-left">
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-700 shrink-0">
                        <Icon className="h-5 w-5" strokeWidth={1.75} />
                      </span>
                      <span className="pf-heading text-base sm:text-lg font-semibold text-slate-900">
                        {s.name}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 pl-14">
                    <p className="text-slate-600 leading-relaxed">{s.detail}</p>
                    {s.perfectFor && (
                      <div className="mt-4">
                        <p className="text-xs tracking-[0.18em] uppercase font-bold text-slate-500 mb-2">
                          Perfect for
                        </p>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                          {s.perfectFor.map((p) => (
                            <li key={p} className="flex items-center gap-2 text-sm text-slate-700">
                              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                              {p}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <Link
                      to={`/quote/${s.slug}`}
                      data-testid={`service-accordion-cta-${i}`}
                      className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-blue-700 transition-colors"
                    >
                      Get a tailored quote
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      </section>

      {/* SECTION 4 — WHY CHOOSE US (dark band) */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-amber-400">
                Why Choose The Phoenix Fix
              </p>
              <h2
                data-testid="whyus-title"
                className="pf-heading mt-3 text-4xl sm:text-5xl font-semibold tracking-tight"
              >
                Your reliable local handyman team.
              </h2>
              <p className="mt-6 text-slate-300 leading-relaxed">
                One trusted team — not five different contractors to chase.
                Every skill you need under one roof, with one point of contact,
                one transparent quote, and one crew that treats your home like
                their own.
              </p>
              <Link
                to="/booking"
                data-testid="whyus-cta"
                className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white text-slate-900 font-semibold hover:bg-amber-400 transition-colors"
              >
                Schedule a visit
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="lg:col-span-7">
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {whyUs.map((item, i) => (
                  <li
                    key={item}
                    data-testid={`whyus-item-${i}`}
                    className="flex items-start gap-3 rounded-2xl p-5 border border-white/10 bg-white/[0.04] backdrop-blur"
                  >
                    <CheckCircle2 className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" />
                    <span className="text-slate-100">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 5 — WHO WE HELP */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="text-center max-w-3xl mx-auto">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">
            Who We Help
          </p>
          <h2
            data-testid="personas-title"
            className="pf-heading mt-3 text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900"
          >
            Services for every situation.
          </h2>
          <p className="mt-5 text-slate-600">
            Whether you’re settling in, settling up, or settling between
            tenants — we’re built for the move.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {personas.map((p, i) => {
            const Icon = p.icon;
            return (
              <Link
                to={`/quote/${p.slug}`}
                key={p.label}
                data-testid={`persona-${i}`}
                className="group bg-white border border-slate-100 rounded-2xl p-6 hover:border-blue-300 hover:shadow-[0_14px_40px_rgb(0,0,0,0.08)] hover:-translate-y-1 active:translate-y-0 transition-all duration-200 flex flex-col cursor-pointer"
              >
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-700 group-hover:bg-blue-700 group-hover:text-white transition-colors">
                  <Icon className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <h3 className="pf-heading mt-4 text-lg font-semibold text-slate-900">
                  {p.label}
                </h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">
                  {p.body}
                </p>
              </Link>
            );
          })}
        </div>
      </section>

      {/* SECTION 6 — REVIEWS */}
      <section className="bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">
              Reviews
            </p>
            <h2
              data-testid="reviews-title"
              className="pf-heading mt-3 text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900"
            >
              What our customers say.
            </h2>

            <div
              data-testid="reviews-aggregate"
              className="mt-6 inline-flex items-center gap-3 rounded-full bg-slate-50 border border-slate-200 px-5 py-2"
            >
              <Stars />
              <span className="pf-heading text-base font-semibold text-slate-900">
                {REVIEW_STATS.average.toFixed(1)}
              </span>
              <span className="text-sm text-slate-500">
                · {REVIEW_STATS.total} reviews
              </span>
            </div>
          </div>

          <ReviewsCarousel />

          <p className="mt-6 text-center text-xs text-slate-400">
            Showing recent verified reviews. Google reviews coming soon.
          </p>
        </div>
      </section>

      {/* SECTION 7 — FINAL CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24">
        <div className="rounded-3xl bg-slate-900 text-white p-10 md:p-16 text-center relative overflow-hidden">
          <div className="absolute inset-0 pf-grid-bg opacity-[0.04]" />
          <div className="relative">
            <h2
              data-testid="final-cta-title"
              className="pf-heading text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight"
            >
              Ready to cross those projects off your list?
            </h2>
            <p className="mt-5 text-slate-300 max-w-2xl mx-auto leading-relaxed">
              Tell us what needs to be done and we’ll help you get it handled.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link
                to="/booking"
                data-testid="final-cta-quote"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-xl bg-white text-slate-900 font-semibold hover:bg-amber-400 transition-colors"
              >
                Request Your Free Quote
                <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href={BUSINESS.phoneHref}
                data-testid="final-cta-call"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-xl bg-transparent text-white border border-white/30 font-semibold hover:bg-white/10 transition-colors"
              >
                <Phone className="h-4 w-4" />
                {BUSINESS.phone}
              </a>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
